import { FC } from 'react'
import useFormProcessor from '@hooks/useFormProcessor'
import { usePostCourseMutation, usePutCourseMutation } from '@services/courses'
import {
  usePostMyCourseMutation,
  usePutMyCourseMutation,
} from '@services/myCourses'
import { CommonCourseBody, CourseDetailed } from '@models/course'
import {
  CreateMyCourseBody,
  MyCourseDetailed,
  UpdateMyCourseBody,
} from '@models/myCourse'
import AddNewItemModal from '@templates/AddNewItemModal'
import OnboardingCodes from '@enums/OnboardingCodes'
import useMe from '@hooks/useMe'
import useCourseFormFields from './useCourseFormFields'
import getDefaultValues from './getDefaultValues'
import { CourseFormValues, CourseFormModalProps } from './types'
import { ONBOARDING_STEPS, ONBOARDING_TITLE } from './onboarding'

type WatchedValues = {
  free: boolean
  safeProgram?: boolean
  electricalSafetyFirstGroup?: boolean
}

type InternalCourseBody = Omit<CreateMyCourseBody, 'published'>
type CourseFormModalComponent = FC<CourseFormModalProps> & { modalWidth?: string }

const COURSE_FORM_MODAL_WIDTH = 'min(1380px, calc(100vw - 90px))'

const extractIdFromIri = (iri: string): number =>
  Number.parseInt(iri.split('/').pop() || '0', 10)

const buildCourseAvailabilities = (form: CourseFormValues) =>
  (form.courseAvailabilities || [])
    .filter((row) => row.company)
    .map((row) => ({
      company: extractIdFromIri(row.company!.value),
      companyBranch: row.filial ? extractIdFromIri(row.filial.value) : null,
    }))

const getFirstAvailability = (form: CourseFormValues) =>
  buildCourseAvailabilities(form)[0]

const buildInternalCourseBody = (
  form: CourseFormValues,
  existingCourse?: MyCourseDetailed
): InternalCourseBody => ({
  title: form.title,
  hoursCount: +form.hoursCount,
  descriptionLink: existingCourse?.descriptionLink || undefined,
  courseCategory: form.courseCategory?.value,
  free: existingCourse?.free ?? false,
  isClientCourse: true,
  electricalSafetyFirstGroup: form.electricalSafetyFirstGroup ?? false,
  // Владельца курса не отправляем: форма его не редактирует. При создании бэкенд
  // проставляет владельца сам, при сохранении оставляет прежнего. Отправка ломала
  // сохранение куратору, которому курс доступен по блоку «Доступность»: компанию
  // владельца он не видит, ссылка на неё не резолвится и запрос падал в 400.
  mintrudDict: form.mintrudDict?.value || null,
  sortOrder: Number.parseInt(form.sortOrder || '0'),
  courseAvailabilities: buildCourseAvailabilities(form),
})

const buildCreateInternalCourseBody = (
  form: CourseFormValues
): CreateMyCourseBody => ({
  ...buildInternalCourseBody(form),
  published: false,
})

const buildUpdateInternalCourseBody = (
  form: CourseFormValues,
  course: MyCourseDetailed
): UpdateMyCourseBody => ({
  ...buildInternalCourseBody(form, course),
  id: course.id,
})

const buildCatalogCourseBody = (
  form: CourseFormValues,
  watched: WatchedValues,
  course?: CourseDetailed
): CommonCourseBody => ({
  descriptionLink: form.descriptionLink,
  title: form.title,
  hoursCount: +form.hoursCount,
  courseCategory: form.courseCategory!.value,
  type: form.type.value,
  subType: form.subType.value,
  published: course?.published ?? false,
  free: watched.free,
  safeProgram: watched.safeProgram ?? false,
  company: (() => {
    const firstAvailability = getFirstAvailability(form)
    return firstAvailability ? `/api/companies/${firstAvailability.company}` : null
  })(),
  companyBranch: (() => {
    const firstAvailability = getFirstAvailability(form)
    return firstAvailability?.companyBranch
      ? `/api/company_branches/${firstAvailability.companyBranch}`
      : null
  })(),
  mintrudDict: form.mintrudDict?.value || null,
  electricalSafetyFirstGroup: watched.electricalSafetyFirstGroup ?? false,
  courseAvailabilities: buildCourseAvailabilities(form),
  sortOrder: Number.parseInt(form.sortOrder || '0'),
})

const setCourseAvailabilityError = (
  methods: ReturnType<typeof useFormProcessor>
) => {
  methods.setError('courseAvailabilities', {
    type: 'manual',
    message: 'Заполните хотя бы одну пару Компания / Филиал',
  })
}

const CourseFormModal: CourseFormModalComponent = ({
  isEdit = false,
  handleCloseModal,
  internalCourseMode = false,
  course,
}) => {
  const { isCompanyGroupCurator, isCompanyCurator, isFilialCurator } = useMe()
  const canSeeOnboarding =
    internalCourseMode &&
    (isCompanyGroupCurator || isCompanyCurator || isFilialCurator)
  const [createCatalogCourse, createCatalogData] = usePostCourseMutation()
  const [updateCatalogCourse, updateCatalogData] = usePutCourseMutation()
  const [createInternalCourse, createInternalData] = usePostMyCourseMutation()
  const [updateInternalCourse, updateInternalData] = usePutMyCourseMutation()

  const getMutationData = () => {
    if (internalCourseMode) {
      return isEdit ? updateInternalData : createInternalData
    }
    return isEdit ? updateCatalogData : createCatalogData
  }

  const additionalProps = getMutationData()

  const methods = useFormProcessor({
    apiErrors: additionalProps.error,
    defaultValues: getDefaultValues(course, internalCourseMode),
  })

  const fields = useCourseFormFields(methods, { internalCourseMode })

  const watched: WatchedValues = {
    free: methods.watch('free'),
    safeProgram: methods.watch('safeProgram'),
    electricalSafetyFirstGroup: methods.watch('electricalSafetyFirstGroup'),
  }

  const submitInternalCourse = (form: CourseFormValues) => {
    if (isEdit && course) {
      updateInternalCourse(
        buildUpdateInternalCourseBody(form, course as MyCourseDetailed)
      )
      return
    }

    createInternalCourse(buildCreateInternalCourseBody(form))
  }

  const submitCatalogCourse = (form: CourseFormValues) => {
    const body = buildCatalogCourseBody(form, watched, course as CourseDetailed)

    if (isEdit && course) {
      updateCatalogCourse({ ...body, id: (course as CourseDetailed).id })
      return
    }

    createCatalogCourse(body)
  }

  const handleSubmitForm = (form: CourseFormValues) => {
    if (internalCourseMode && buildCourseAvailabilities(form).length === 0) {
      setCourseAvailabilityError(methods)
      return
    }

    if (internalCourseMode) {
      submitInternalCourse(form)
      return
    }

    submitCatalogCourse(form)
  }

  const titlePostfix = internalCourseMode && !isEdit ? 'внутренний курс' : 'курс'
  const successAddTitle = internalCourseMode ? 'Курс успешно создан' : 'Курс успешно добавлен'

  return (
    <AddNewItemModal
      {...additionalProps}
      handleCloseModal={handleCloseModal}
      onSubmitForm={handleSubmitForm}
      methods={methods}
      fields={fields}
      isEdit={isEdit}
      titlePostfix={titlePostfix}
      successContentAddTitle={successAddTitle}
      successContentEditTitle="Курс успешно изменен"
      onboarding={
        canSeeOnboarding
          ? {
              steps: ONBOARDING_STEPS,
              firstStepTitle: ONBOARDING_TITLE,
              code: OnboardingCodes.my_courses_create,
              withScroll: true,
              withFeedback: false,
            }
          : undefined
      }
    />
  )
}

CourseFormModal.modalWidth = COURSE_FORM_MODAL_WIDTH

export default CourseFormModal
